import { api } from "./client";

export interface ApiKeyItem {
  key_id: string;
  name: string;
  prefix: string;
  scopes: string[];
  rate_limit_per_minute: number;
  status: "active" | "revoked" | "expired";
  created_at: string;
  expires_at?: string | null;
  last_used_at?: string | null;
  revoked_at?: string | null;
  created_by?: string | null;
}

export interface ApiKeyCreatedResponse {
  key_id: string;
  name: string;
  prefix: string;
  secret_key: string;
  scopes: string[];
  rate_limit_per_minute: number;
  status: string;
  created_at: string;
  expires_at?: string | null;
}

export interface CreditBalanceData {
  client_id: string;
  currency: string;
  balance_paise: number;
  balance_inr: number;
  price_per_request_paise: number;
  price_per_request_inr: number;
  pricing_policy: string;
}

export interface UsageTimelineItem {
  hour: string;
  requests: number;
  billable: number;
  amount_paise: number;
}

export interface UsageSummaryData {
  period_start: string;
  period_end: string;
  total_requests: number;
  billable_requests: number;
  non_billable_requests: number;
  successful_requests: number;
  failed_requests: number;
  rate_limit_events: number;
  total_amount_paise: number;
  total_amount_inr: number;
  avg_latency_ms: number;
  p95_latency_ms: number;
  remaining_credits_paise: number;
  remaining_credits_inr: number;
  requests_by_endpoint: Record<string, number>;
  requests_by_key: Record<string, number>;
  timeline: UsageTimelineItem[];
}

export interface UsageEventLogItem {
  event_id: string;
  request_id: string;
  key_id: string;
  endpoint: string;
  http_method: string;
  status_code: number;
  duration_ms: number;
  billable: boolean;
  price_paise: number;
  idempotency_key?: string | null;
  error_code?: string | null;
  timestamp: string;
}

export async function fetchApiKeys(): Promise<ApiKeyItem[]> {
  const res = await api.get("/api/v1/keys");
  return res.data?.data || [];
}

export async function createApiKey(payload: {
  name: string;
  scopes?: string[];
  rate_limit_per_minute?: number;
  expires_in_days?: number | null;
}): Promise<{ data: ApiKeyCreatedResponse; warning: string }> {
  const res = await api.post("/api/v1/keys", payload);
  return res.data;
}

export async function revokeApiKey(keyId: string): Promise<{ message: string }> {
  const res = await api.post(`/api/v1/keys/${keyId}/revoke`);
  return res.data;
}

export async function fetchCreditBalance(): Promise<CreditBalanceData> {
  const res = await api.get("/api/v1/billing/balance");
  return res.data?.data;
}

export async function topupCredits(amountInr: number, paymentReference?: string): Promise<any> {
  const res = await api.post("/api/v1/billing/topup", {
    amount_inr: amountInr,
    payment_reference: paymentReference || `portal_topup_${Date.now()}`
  });
  return res.data;
}

export async function fetchUsageSummary(days: number = 30, keyId?: string): Promise<UsageSummaryData> {
  const params: any = { days };
  if (keyId) params.key_id = keyId;
  const res = await api.get("/api/v1/usage", { params });
  return res.data?.data;
}

export async function fetchUsageEvents(limit: number = 50, keyId?: string): Promise<UsageEventLogItem[]> {
  const params: any = { limit };
  if (keyId) params.key_id = keyId;
  const res = await api.get("/api/v1/usage/events", { params });
  return res.data?.data || [];
}

export interface SubscriptionUsageData {
  client_id: string;
  plan: string;
  role: string;
  is_eligible: boolean;
  entitlement_status: "active" | "inactive";
  reason: string;
  quota: number;
  used: number;
  remaining: number;
  usage_percent: number;
  period_start: string;
  period_end: string;
  period_key: string;
  reset_timestamp: string;
  reset_seconds: number;
  days_remaining: number;
  warning_level: "normal" | "warning_80" | "critical_95" | "exhausted_100";
}

export async function fetchSubscriptionUsage(): Promise<SubscriptionUsageData> {
  const res = await api.get("/api/v1/subscription/usage");
  return res.data?.data;
}
